<template>
  <div class="app">
    <header class="header">
      <h1 class="title">WeatherNow 🌤️</h1>
      <div class="search-box">
        <input
          v-model="query"
          @keyup.enter="searchWeather"
          type="text"
          placeholder="Enter city..."
        />
        <button @click="searchWeather">Search</button>
      </div>
    </header>

    <main class="main">
      <!-- Show weather card if data exists -->
      <div v-if="weather" class="weather-card">
        <h2>{{ weather.name }}, {{ weather.sys.country }}</h2>
        <p class="date">{{ formatDate(new Date()) }}</p>

        <div class="temp-section">
          <img
            :src="'https://openweathermap.org/img/wn/' + weather.weather[0].icon + '@2x.png'"
            :alt="weather.weather[0].description"
          />
          <h1>{{ Math.round(weather.main.temp) }}°C</h1>
        </div>

        <p class="condition">{{ weather.weather[0].main }}</p>
        <p>Feels like: {{ Math.round(weather.main.feels_like) }}°C</p>
        <p>Humidity: {{ weather.main.humidity }}%</p>
        <p>Wind: {{ weather.wind.speed }} km/h</p>
      </div>

      <!-- Show error if city not found -->
      <div v-else-if="error" class="placeholder">
        <p>{{ error }}</p>
      </div>

      <!-- Placeholder before search -->
      <div v-else class="placeholder">
        <p>Search for a city to see the weather 🌍</p>
      </div>
    </main>

    <footer class="footer">
  <p>
    Powered by 
    <a href="https://openweathermap.org/" target="_blank" rel="noopener noreferrer">
      OpenWeatherMap
    </a>
    | Built by <strong>MykelCodes</strong> 💻
  </p>
</footer>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const query = ref('')
const weather = ref(null)
const error = ref('') // <-- New reactive error variable
const apiKey = 'ed80a49c1cd49c2b766470ebb5e6149c' // replace with your valid key

async function searchWeather() {
  error.value = ''  // reset error
  if (!query.value) return

  const city = query.value.trim()
  const url = `https://api.openweathermap.org/data/2.5/weather?q=${city}&units=metric&appid=${apiKey}`

  try {
    const res = await fetch(url)
    if (!res.ok) {
      const errData = await res.json()
      throw new Error(errData.message || 'City not found')
    }
    weather.value = await res.json()
    query.value = ''
  } catch (err) {
    weather.value = null
    error.value = 'City not found 😕'
  }
}

function formatDate(d) {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
}
</script>


<style>
/* ======== Base Layout ======== */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Poppins", sans-serif;
}

body, html, #app {
  height: 100%;
  background: linear-gradient(to bottom right, #57c1eb, #246fa8);
  color: #fff;
}

/* ======== App Container ======== */
.app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* ======== Header ======== */
.header {
  text-align: center;
  padding: 20px;
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
}

.title {
  font-size: 2rem;
  margin-bottom: 10px;
}

.search-box {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.search-box input {
  padding: 10px 14px;
  border: none;
  border-radius: 6px;
  width: 200px;
  outline: none;
}

.search-box button {
  padding: 10px 14px;
  border: none;
  border-radius: 6px;
  background-color: #fff;
  color: #246fa8;
  cursor: pointer;
  font-weight: bold;
  transition: 0.3s;
}

.search-box button:hover {
  background-color: #e0e0e0;
}

/* ======== Main Weather Card ======== */
.main {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.weather-card {
  text-align: center;
  background-color: rgba(255, 255, 255, 0.15);
  padding: 30px;
  border-radius: 16px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  width: 300px;
  transition: transform 0.3s ease;
}

.weather-card:hover {
  transform: translateY(-5px);
}

.temp-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.temp-section img {
  width: 100px;
  height: 100px;
}

.temp-section h1 {
  font-size: 3rem;
  margin-top: -10px;
}

.condition {
  font-size: 1.4rem;
  font-weight: 600;
  margin: 10px 0;
}

.placeholder {
  text-align: center;
  font-size: 1.2rem;
  opacity: 0.8;
}

/* ======== Footer ======== */
.footer {
  text-align: center;
  padding: 10px;
  background-color: rgba(255, 255, 255, 0.1);
}

.footer a {
  color: #fff;
  text-decoration: underline;
}
</style>
